import type { Archive, ChatMessage } from "../../domain/types";
import { callLLMWithRoutes } from "../../services/llm";
import {
  resolveFeatureRoutes,
  type FeatureRoutingSettings,
} from "../../services/featureRoutes";

interface CompletedTurn {
  messages: ChatMessage[];
  throughMessageId: string;
}

function completedTurns(messages: ChatMessage[]): CompletedTurn[] {
  const turns: CompletedTurn[] = [];
  let pending: ChatMessage[] = [];
  for (const message of messages) {
    if (message.role === "system") continue;
    pending.push(message);
    if (message.role === "assistant") {
      turns.push({ messages: pending, throughMessageId: message.id });
      pending = [];
    }
  }
  return turns;
}

export function deepSummaryTarget(archive: Archive) {
  const turns = completedTurns(archive.messages);
  const coverable = Math.max(0, turns.length - 8);
  if (archive.deepSummary.coveredTurns === 0)
    return turns.length >= 40 ? coverable : null;
  return coverable - archive.deepSummary.coveredTurns >= 16 ? coverable : null;
}

function retainedCharacterRatio(previous: string, next: string) {
  const previousChars = new Set([...previous.replace(/[\s\p{P}\p{S}]/gu, "")]);
  if (!previousChars.size) return 1;
  const nextChars = new Set([...next.replace(/[\s\p{P}\p{S}]/gu, "")]);
  return (
    [...previousChars].filter((character) => nextChars.has(character)).length /
    previousChars.size
  );
}

export function validateDeepSummary(text: string, previous = "") {
  const normalized = text.trim();
  if (normalized.length < 300) throw new Error("深度总结少于 300 字符");
  const segments = normalized
    .split(/\n\s*\n|(?=^#{1,3}\s)/m)
    .filter((segment) => segment.trim().length >= 30);
  if (segments.length < 3) throw new Error("深度总结少于 3 个有效分段");
  if (
    previous.length >= 100 &&
    retainedCharacterRatio(previous, normalized) < 0.3
  )
    throw new Error("深度总结未保留至少 30% 的既有信息字符");
  return normalized;
}

function renderTurns(turns: CompletedTurn[]) {
  return turns
    .map((turn, index) =>
      turn.messages
        .map(
          (message) =>
            `[${index + 1}/${message.role === "user" ? "玩家" : "主持"}] ${message.content}`,
        )
        .join("\n"),
    )
    .join("\n\n");
}

export async function settleDeepSummaryForTurn(
  archive: Archive,
  settings: FeatureRoutingSettings,
): Promise<Archive> {
  const target = deepSummaryTarget(archive);
  if (target === null) return archive;
  const turns = completedTurns(archive.messages);
  const start = Math.min(archive.deepSummary.coveredTurns, target);
  const source = turns.slice(start, target);
  if (!source.length) return archive;
  const previous = archive.deepSummary.text;
  const routes = resolveFeatureRoutes("deep-summary", settings).map(
    (route) => ({
      ...route,
      id: `deep-summary:${route.id ?? route.model}`,
      temperature: 0.2,
      retries: 0,
    }),
  );
  let feedback = "";
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const response = await callLLMWithRoutes(
        routes,
        [
          {
            role: "system",
            content:
              "你负责压缩 MH-RP 的较早对话历史。仅记录已经发生的事实、人物关系变化、任务与怪物状态、未解决伏笔；不得续写。输出简体中文，至少 300 字符，使用至少三个带标题的分段。必须保留既有摘要中的关键事实。",
          },
          {
            role: "user",
            content: `【既有摘要】\n${previous || "无"}\n\n【新增待压缩回合】\n${renderTurns(source)}${feedback ? `\n\n【上次校验失败】${feedback}` : ""}`,
          },
        ],
        { stream: false },
      );
      const text = validateDeepSummary(response.text, previous);
      const next = structuredClone(archive);
      next.deepSummary = {
        text,
        coveredThroughMessageId: turns[target - 1]?.throughMessageId ?? null,
        coveredTurns: target,
        updatedAt: Date.now(),
        revision: archive.deepSummary.revision + 1,
      };
      return next;
    } catch (error) {
      feedback = error instanceof Error ? error.message : String(error);
    }
  }
  return archive;
}
